/**
 * Verdiva Theme - Global JavaScript
 *
 * Core functionality for the theme including:
 * - Sticky header
 * - Mobile menu
 * - Cart drawer
 * - Announcements
 * - Utility functions
 */

(function() {
  'use strict';

  // ==========================================================================
  // Utility Functions
  // ==========================================================================

  const debounce = (fn, delay) => {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  // Prijzen zonder euroteken, hele bedragen als 136,- (zie snippets/money.liquid)
  const formatMoney = (cents) => {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return value.endsWith(',00') ? value.slice(0, -3) + ',-' : value;
  };

  // ==========================================================================
  // Header
  // ==========================================================================

  const initHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    let lastScroll = window.scrollY;
    const scrollThreshold = 10;
    // Pas verbergen voorbij deze hoogte: bovenaan de pagina blijft de balk altijd staan,
    // anders knippert hij bij het kleinste schokje direct onder de rand.
    const hideAfter = 160;
    // Kleine bewegingen (trackpad-ruis, adresbalk van mobiel Safari die in-/uitschuift)
    // mogen de balk niet laten flipperen.
    const minDelta = 6;

    // Niet verbergen zolang er iets uit de header openstaat: het mega menu, de
    // zoeksuggesties of het mobiele menu. Toetsenbordfocus in de balk telt ook mee.
    const isBusy = () =>
      header.classList.contains('has-mega-open') ||
      header.classList.contains('has-search-open') ||
      header.contains(document.activeElement) ||
      !!document.querySelector('#mobile-menu.is-open');

    const handleScroll = () => {
      const currentScroll = Math.max(window.scrollY, 0);

      // Add scrolled class for styling
      if (currentScroll > scrollThreshold) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }

      const delta = currentScroll - lastScroll;
      if (Math.abs(delta) < minDelta) return; // lastScroll blijft staan: kleine stapjes tellen op

      // Een sprong groter dan een schermhoogte is geen scrollbeweging maar een
      // verspringing: een ankerlink, of de popup die de scroll-lock loslaat en de
      // pagina terugzet. Alleen de positie bijwerken, de balk laten staan.
      if (Math.abs(delta) > window.innerHeight) {
        lastScroll = currentScroll;
        return;
      }

      if (currentScroll <= hideAfter || delta < 0 || isBusy()) {
        header.classList.remove('is-hidden');
      } else {
        header.classList.add('is-hidden');
      }

      lastScroll = currentScroll;
    };

    // rAF-throttle i.p.v. debounce: werkt elke frame bij tijdens het scrollen.
    // (Een debounce wachtte tot het scrollen stopte, waardoor de balk bij een
    //  lange doorlopende scroll pas laat verscheen.)
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        handleScroll();
        ticking = false;
      });
    }, { passive: true });

    // Tabben naar iets in de balk terwijl die verborgen is: meteen terughalen,
    // anders krijgt de bezoeker een onzichtbare focus.
    header.addEventListener('focusin', () => header.classList.remove('is-hidden'));

    handleScroll(); // Initial check
  };

  // ==========================================================================
  // Mobile Menu
  // ==========================================================================

  const initMobileMenu = () => {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const menu = document.getElementById('mobile-menu');
    const closeButtons = document.querySelectorAll('[data-mobile-menu-close]');

    if (!toggle || !menu) return;

    const openMenu = () => {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    };

    const closeMenu = () => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    };

    toggle.addEventListener('click', () => {
      const isOpen = menu.classList.contains('is-open');
      isOpen ? closeMenu() : openMenu();
    });

    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeMenu);
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) {
        closeMenu();
      }
    });

    // Accordion: open/sluit sub-items bij klik op link met chevron
    menu.querySelectorAll('.mobile-menu__item').forEach(item => {
      const link = item.querySelector('.mobile-menu__link');
      const sub  = item.querySelector('.mobile-menu__sub');
      if (!link || !sub) return;

      link.addEventListener('click', (e) => {
        // Alleen accordion-gedrag als er een sub is; anders gewoon navigeren
        e.preventDefault();
        const isOpen = item.classList.contains('is-open');
        // Sluit alle andere open items
        menu.querySelectorAll('.mobile-menu__item.is-open').forEach(other => {
          if (other !== item) other.classList.remove('is-open');
        });
        item.classList.toggle('is-open', !isOpen);
        // Navigeer wel als er geen sub-items zijn
        if (!sub) window.location.href = link.getAttribute('href');
      });
    });
  };

  // ==========================================================================
  // Cart Drawer
  // ==========================================================================

  // ==========================================================================
  // Upsell-regels in de winkelwagen
  //
  // Kleine planten (potmaat onder de 12 cm) zijn alleen te koop als aanvulling
  // op een grotere plant. Ze dragen de property _upsell_voor met de variant-ID
  // van die hoofdplant. Drie regels, allemaal hier afgedwongen:
  //
  //   1. Hoofdplant weg  -> de gekoppelde upsells gaan mee.
  //   2. Minder hoofdplanten -> nooit meer upsells dan hoofdplanten.
  //   3. Upsell zonder geldige hoofdplant -> weg. Dat vangt ook de upsell die
  //      rechtstreeks via /cart/add is toegevoegd, want die heeft de property
  //      helemaal niet; daarvoor staat de variantenlijst in window.theme.
  //
  // Alles gaat in één /cart/update.js, niet per regel. Twee losse aanroepen
  // kunnen elkaar inhalen, en dan zou de tweede met een verouderde winkelwagen
  // werken.
  // ==========================================================================

  const UPSELL_PROP = '_upsell_voor';
  const UPSELL_PARENT_NAME = '_upsell_voor_naam';
  const UPSELL_NOTICE_KEY = 'verdiva:upsell-notice';
  const UPSELL_SYNCED_KEY = 'verdiva:upsell-synced';

  const isUpsellLine = (item) =>
    !!(item.properties && item.properties[UPSELL_PROP]);

  const isUpsellProduct = (item) => {
    const ids = window.theme?.upsellVariants || [];
    return ids.indexOf(String(item.variant_id)) !== -1;
  };

  // Geeft {updates, removed} terug. updates is leeg als er niets hoeft te
  // veranderen -- dan slaan we de hele ronde over.
  const planUpsellUpdates = (cart) => {
    const updates = {};
    const removed = [];

    // Hoeveel hoofdplanten staan er per variant? Alleen regels die zelf geen
    // upsell zijn tellen mee: een upsell kan nooit de ouder van een upsell
    // zijn.
    const mainQty = {};
    cart.items.forEach((item) => {
      if (isUpsellLine(item) || isUpsellProduct(item)) return;
      const id = String(item.variant_id);
      mainQty[id] = (mainQty[id] || 0) + item.quantity;
    });

    cart.items.forEach((item) => {
      if (!isUpsellLine(item) && !isUpsellProduct(item)) return;

      const parentId = isUpsellLine(item) ? String(item.properties[UPSELL_PROP]) : null;
      const allowed = parentId ? (mainQty[parentId] || 0) : 0;

      if (allowed <= 0) {
        updates[item.key] = 0;
        removed.push({
          naam: item.product_title,
          ouder: (item.properties && item.properties[UPSELL_PARENT_NAME]) || ''
        });
      } else if (item.quantity > allowed) {
        updates[item.key] = allowed;
      }
    });

    return { updates, removed };
  };

  const rememberUpsellNotice = (removed) => {
    if (!removed.length) return;
    try {
      sessionStorage.setItem(UPSELL_NOTICE_KEY, JSON.stringify(removed));
    } catch (e) {
      // Privémodus of geblokkeerde opslag: dan missen we alleen de melding.
    }
  };

  // Waar de melding heen kan. Belangrijk is dat het een plek is die de
  // bezoeker óók echt ziet: zodra de laatste plant weg is rendert de
  // cart-pagina geen .cart__items meer, en de drawer staat op elke pagina in
  // de opmaak maar is meestal dicht.
  const upsellNoticeHost = () => {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && drawer.classList.contains('is-open')) {
      return document.getElementById('cart-drawer-body');
    }
    return (
      document.querySelector('.cart__items') ||
      document.querySelector('.cart__empty') ||
      document.querySelector('.cart__wrapper')
    );
  };

  const showUpsellNotice = () => {
    // De melding pas weggooien als hij ergens terecht kan. Anders verdwijnt
    // hij ongezien bij een herlaadbeurt waarin de pagina nog niet klaar is.
    const host = upsellNoticeHost();
    if (!host) return;

    let removed;
    try {
      const raw = sessionStorage.getItem(UPSELL_NOTICE_KEY);
      if (!raw) return;
      sessionStorage.removeItem(UPSELL_NOTICE_KEY);
      removed = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!removed || !removed.length) return;

    const zin = removed
      .map((r) =>
        r.ouder
          ? `<strong>${r.naam}</strong> is ook verwijderd, die hoort bij ${r.ouder}.`
          : `<strong>${r.naam}</strong> is ook verwijderd, die is alleen samen met een grotere plant te bestellen.`
      )
      .join(' ');

    const el = document.createElement('p');
    el.className = 'cart-upsell-notice';
    el.setAttribute('role', 'status');
    el.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="13"/><line x1="12" y1="16" x2="12" y2="16"/></svg><span>' +
      zin +
      '</span>';
    host.prepend(el);

    setTimeout(() => {
      el.classList.add('is-leaving');
      setTimeout(() => el.remove(), 400);
    }, 6000);
  };

  // Zet de regels op volgorde: elke hoofdplant met zijn eigen upsells er
  // direct onder. De winkelwagen zelf staat op volgorde van toevoegen, en
  // daarin kan een upsell ver van zijn plant af komen te staan.
  const orderCartItems = (items) => {
    const byParent = {};
    items.forEach((item) => {
      if (!isUpsellLine(item)) return;
      const parent = String(item.properties[UPSELL_PROP]);
      (byParent[parent] = byParent[parent] || []).push(item);
    });

    const rows = [];
    items.forEach((item) => {
      if (isUpsellLine(item)) return;
      rows.push({ item: item, upsell: false });

      const id = String(item.variant_id);
      (byParent[id] || []).forEach((kind) => {
        rows.push({
          item: kind,
          upsell: true,
          allowed: item.quantity,
          // De korte naam uit de property, want product_title draagt ook de
          // soortaanduiding ("Calathea Warscewiczii - Peacock plant") en dat
          // loopt op mobiel over twee regels. De cart-pagina gebruikt
          // dezelfde bron, zodat beide weergaven hetzelfde zeggen.
          ouder:
            (kind.properties && kind.properties[UPSELL_PARENT_NAME]) ||
            item.product_title
        });
      });
      delete byParent[id];
    });

    // Upsells waarvan de hoofdplant er niet (meer) is. Normaal ruimt
    // syncUpsells die op; mislukt dat, dan horen ze zichtbaar te blijven en
    // niet stilletjes te verdwijnen uit het overzicht.
    Object.keys(byParent).forEach((parent) => {
      byParent[parent].forEach((wees) => {
        rows.push({ item: wees, upsell: true, allowed: 0, ouder: '' });
      });
    });

    return rows;
  };

  // Haalt de winkelwagen op, past de regels toe en geeft de bijgewerkte
  // winkelwagen terug. Zonder wijzigingen komt dezelfde winkelwagen terug.
  const syncUpsells = async (prefetchedCart) => {
    try {
      const cart =
        prefetchedCart ||
        (await fetch(window.theme?.routes?.cart_url + '.js').then((r) => r.json()));

      const { updates, removed } = planUpsellUpdates(cart);
      if (!Object.keys(updates).length) return cart;

      const response = await fetch(window.theme?.routes?.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('cart update');

      rememberUpsellNotice(removed);
      return await response.json();
    } catch (error) {
      console.error('Upsell-sync mislukt:', error);
      return prefetchedCart || null;
    }
  };

  // De cart-pagina is server-side gerenderd, dus na een correctie moet de
  // pagina opnieuw. De vlag voorkomt dat een correctie die blijft terugkomen
  // de pagina in een herlaadlus duwt.
  const initCartPageUpsellSync = async () => {
    // Niet op .cart__items aanhaken: haalt de laatste hoofdplant zijn upsell
    // mee, dan is de winkelwagen leeg en rendert die lijst niet meer -- juist
    // dan moet de melding nog verschijnen.
    if (!document.querySelector('.cart__wrapper')) return;

    if (!document.querySelector('.cart__items')) {
      showUpsellNotice();
      return;
    }

    let alreadySynced = false;
    try {
      alreadySynced = sessionStorage.getItem(UPSELL_SYNCED_KEY) === '1';
    } catch (e) {}

    if (alreadySynced) {
      try { sessionStorage.removeItem(UPSELL_SYNCED_KEY); } catch (e) {}
      showUpsellNotice();
      return;
    }

    try {
      const cart = await fetch(window.theme?.routes?.cart_url + '.js').then((r) => r.json());
      const { updates } = planUpsellUpdates(cart);
      if (!Object.keys(updates).length) {
        showUpsellNotice();
        return;
      }
      await syncUpsells(cart);
      try { sessionStorage.setItem(UPSELL_SYNCED_KEY, '1'); } catch (e) {}
      window.location.reload();
    } catch (error) {
      console.error('Upsell-sync op de cart-pagina mislukt:', error);
    }
  };

  const initCartDrawer = () => {
    const drawer = document.getElementById('cart-drawer');
    const toggleButtons = document.querySelectorAll('[data-cart-toggle]');
    const closeButtons = document.querySelectorAll('[data-cart-drawer-close]');

    if (!drawer) return;

    const openDrawer = async () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      // Eerst de regels toepassen, dan pas tekenen -- anders ziet de bezoeker
      // een upsell staan die een tel later verdwijnt.
      const cart = await syncUpsells();
      await refreshCartDrawer(cart);
      showUpsellNotice();
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    toggleButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        // Check if cart type is drawer
        if (window.theme?.settings?.cart_type === 'page') {
          window.location.href = window.theme.routes.cart_url;
          return;
        }
        openDrawer();
      });
    });

    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeDrawer);
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    // Expose globally
    window.cartDrawer = {
      open: openDrawer,
      close: closeDrawer
    };
  };

  const refreshCartDrawer = async (prefetchedCart) => {
    const body = document.getElementById('cart-drawer-body');
    const subtotal = document.getElementById('cart-drawer-subtotal');

    if (!body) return;

    try {
      // Reuse a cart object the caller already has (e.g. the /cart/change.js
      // response) to avoid a redundant /cart.js round-trip.
      const cart = prefetchedCart || await fetch(window.theme?.routes?.cart_url + '.js').then(r => r.json());

      // Update all cart-count badges (header, mobile, …) - this makes a separate
      // updateCartCount() fetch unnecessary after a refresh.
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count > 0 ? cart.item_count : '';
      });
      if (window.theme) window.theme.cartItemCount = cart.item_count;

      // Update upsell
      const upsellEl = document.getElementById('cd-upsell-wrap');
      if (upsellEl) {
        const threshold2 = (window.theme && window.theme.freeShippingThreshold) ? window.theme.freeShippingThreshold : 7500;
        const needed2    = threshold2 - cart.total_price;
        if (needed2 > 0) {
          try {
            const allProducts  = JSON.parse(document.getElementById('upsell-products-data').textContent);
            const cartIds      = cart.items.map(i => i.product_id);
            const candidates   = allProducts.filter(p => !cartIds.includes(p.id));
            const smart        = candidates.filter(p => p.price >= needed2).slice(0, 3);
            const fill2        = smart.length < 3 ? smart.concat(candidates.filter(p => p.price < needed2 && !smart.find(s => s.id === p.id)).slice(0, 3 - smart.length)) : smart;
            const top3         = fill2.slice(0, 3);
            if (top3.length > 0) {
              upsellEl.innerHTML = '<p class="cd-upsell__title">Tip: voeg dit toe voor gratis verzending</p>' +
                top3.map(p => {
                  return `<div class="cd-upsell__item" role="button" tabindex="0" data-variant-id="${p.variant_id}" data-product-id="${p.id}">
                    <img src="${p.image}" alt="" class="cd-upsell__img" loading="lazy">
                    <div class="cd-upsell__info">
                      <span class="cd-upsell__name">${p.title}</span>
                      <span class="cd-upsell__price">${formatMoney(p.price)}</span>
                    </div>
                    <button type="button" class="cd-upsell__add" tabindex="-1" aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                  </div>`;
                }).join('');
              upsellEl.style.display = 'block';
            } else {
              upsellEl.style.display = 'none';
            }
          } catch(e) { upsellEl.style.display = 'none'; }
        } else {
          upsellEl.style.display = 'none';
        }
      }

      // Update shipping bar
      const threshold = (window.theme && window.theme.freeShippingThreshold) ? window.theme.freeShippingThreshold : 7500;
      const needed    = Math.max(0, threshold - cart.total_price);
      const pct       = Math.min(100, (cart.total_price / threshold) * 100);
      const fill      = document.getElementById('cd-ship-fill');
      const msg       = document.getElementById('cd-ship-msg');
      if (fill) fill.style.width = pct + '%';
      if (msg) {
        if (needed <= 0) {
          msg.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg><span>Je hebt <strong>gratis verzending</strong>!</span>';
        } else {
          // Zelfde opmaak als snippets/money.liquid, want deze balk staat ook
          // server-side in layout/theme.liquid: stond er een euroteken in de
          // JS-versie, dan sprong het bedrag om zodra de drawer hertekende.
          msg.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg><span>Nog <strong>' + formatMoney(needed) + '</strong> voor gratis verzending</span>';
        }
      }

      // Update subtotal
      if (subtotal) {
        subtotal.textContent = formatMoney(cart.total_price);
      }

      // Update body
      if (cart.item_count === 0) {
        body.innerHTML = `
          <div class="cart-drawer__empty">
            <p>${window.theme?.strings?.cartEmpty || 'Your cart is empty'}</p>
          </div>
        `;
      } else {
        // Bug A extra guard: items met quantity 0 nooit renderen in de drawer
        body.innerHTML = orderCartItems(cart.items).filter(row => row.item.quantity > 0).map(row => {
          const item = row.item;
          const capped = row.upsell && item.quantity >= row.allowed;
          return `
          <div class="cart-item${row.upsell ? ' cart-item--upsell' : ''}" data-line-item-key="${item.key}">
            <div class="cart-item__image-wrapper">
              ${item.image ? `<img src="${item.image}" alt="${item.title}" class="cart-item__image" width="100" height="100">` : ''}
            </div>
            <div class="cart-item__details">
              <div class="cart-item__header">
                <h3 class="cart-item__title">
                  <a href="${item.url}">${item.product_title}</a>
                </h3>
                <button type="button" class="cart-item__remove" data-remove-item data-line-key="${item.key}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                </button>
              </div>
              ${row.upsell && row.ouder ? `<p class="cart-item__upsell-label">Bij ${row.ouder}</p>` : ''}
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
              ${(item.line_level_discount_allocations || []).map(d => `<span class="cart-item__discount">✓ ${d.discount_application.title} (−${formatMoney(d.amount)})</span>`).join('')}
              <div class="cart-item__footer">
                <div class="cart-item__quantity">
                  <button type="button" class="cart-item__quantity-btn" data-quantity-change="-1" data-line-key="${item.key}" aria-label="Verlaag aantal of verwijder">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  </button>
                  <span class="cart-item__quantity-value">${item.quantity}</span>
                  <button type="button" class="cart-item__quantity-btn" data-quantity-change="1" data-line-key="${item.key}"${capped ? ' disabled aria-disabled="true" title="Niet meer dan het aantal hoofdplanten"' : ''}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  </button>
                </div>
                <div class="cart-item__price">
                  <span class="cart-item__price-current">${formatMoney(item.final_line_price)}</span>
                </div>
              </div>
            </div>
          </div>
        `;
        }).join('');

        // Re-attach event listeners
        attachCartItemListeners();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  };

  // Eén set gedelegeerde listeners voor verwijder-/aantal-knoppen. Werkt
  // daardoor zowel voor de server-gerenderde cart-pagina als voor items die
  // de drawer later via JS rendert (geen re-binding per render meer nodig).
  let cartItemListenersAttached = false;
  const attachCartItemListeners = () => {
    if (cartItemListenersAttached) return;
    cartItemListenersAttached = true;

    // Huidig aantal: de drawer toont een <span>, de cart-pagina een <input>
    const readQuantity = (item) => {
      const valueEl = item.querySelector('.cart-item__quantity-value');
      if (valueEl) return parseInt(valueEl.textContent);
      const inputEl = item.querySelector('.cart-item__quantity-input');
      return inputEl ? parseInt(inputEl.value) : NaN;
    };

    document.addEventListener('click', async (e) => {
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        const key = removeBtn.dataset.lineKey;
        if (key) await updateCartItem(key, 0);
        return;
      }

      const qtyBtn = e.target.closest('[data-quantity-change]');
      if (qtyBtn) {
        const change = parseInt(qtyBtn.dataset.quantityChange);
        const key = qtyBtn.dataset.lineKey;
        const item = qtyBtn.closest('.cart-item');
        if (!key || !item) return;
        const currentQty = readQuantity(item);
        if (isNaN(currentQty)) return;
        const newQty = currentQty + change;
        // Bug A fix: zodra qty naar 0 (of lager) gaat, verwijder het item meteen
        // uit de UI vóór de change-call (voorkomt "€0,00-geest"-regel).
        if (newQty <= 0) {
          item.remove();
          await updateCartItem(key, 0);
          return;
        }
        await updateCartItem(key, newQty);
      }
    });

    // Aantal direct intypen op de cart-pagina
    document.addEventListener('change', async (e) => {
      const input = e.target.closest('[data-quantity-input]');
      if (!input) return;
      const key = input.dataset.lineKey;
      const qty = parseInt(input.value);
      if (!key || isNaN(qty)) return;
      await updateCartItem(key, Math.max(1, qty));
    });
  };

  const updateCartItem = async (key, quantity) => {
    try {
      const response = await fetch(window.theme?.routes?.cart_change_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) throw new Error('Failed to update cart');

      // /cart/change.js returns the full updated cart - reuse it instead of
      // firing two more /cart.js fetches.
      const cart = await response.json();

      // Meteen de upsell-regels erop, vóór we herladen of hertekenen: een
      // hoofdplant die net is weggehaald moet zijn upsells in dezelfde
      // beweging meenemen.
      const synced = await syncUpsells(cart);

      // Op de cart-pagina is de HTML server-side gerenderd: herladen zodat
      // items, prijzen en totalen kloppen. In de drawer volstaat re-renderen.
      if (document.querySelector('.cart__items')) {
        window.location.reload();
        return;
      }

      await refreshCartDrawer(synced || cart);
      showUpsellNotice();
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch(window.theme?.routes?.cart_url + '.js');
      const cart = await response.json();

      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count > 0 ? cart.item_count : '';
      });

      window.theme.cartItemCount = cart.item_count;
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  };

  // ==========================================================================
  // Quick Add to Cart
  // ==========================================================================

  const initQuickAdd = () => {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-variant-id].product-card__quick-add, [data-variant-id].pc__add');
      if (!btn) return;

      e.preventDefault();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const originalHTML = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" style="animation:pcSpin .6s linear infinite"><path d="M12 2a10 10 0 0 1 10 10"/></svg>';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>';
        btn.style.background = '#1B3B36';

        // Opening the drawer refreshes it, which updates the cart-count badges too,
        // so there's no need for a separate (blocking) updateCartCount() fetch first.
        if (window.cartDrawer) {
          window.cartDrawer.open();
        } else {
          updateCartCount();
        }

        setTimeout(() => {
          btn.innerHTML = originalHTML;
          btn.style.background = '';
          btn.disabled = false;
        }, 1400);

      } catch (error) {
        console.error('Error adding to cart:', error);
        btn.innerHTML = originalHTML;
        btn.disabled = false;
      }
    });
  };

  // ==========================================================================
  // Cart Drawer Upsell - add recommended product without leaving the drawer
  // ==========================================================================

  const initCartUpsellAdd = () => {
    // Announce via the drawer's polite live region. Clear first so repeated
    // messages are re-announced by screen readers.
    const announce = (msg) => {
      const region = document.getElementById('cart-drawer-status');
      if (!region) return;
      region.textContent = '';
      setTimeout(() => { region.textContent = msg; }, 60);
    };

    const resetCard = (item, btn, originalHTML) => {
      if (btn) btn.innerHTML = originalHTML;
      item.style.pointerEvents = '';
      item.dataset.adding = 'false';
    };

    const handleAdd = async (item, viaKeyboard) => {
      const variantId = item.dataset.variantId;
      if (!variantId || item.dataset.adding === 'true') return;
      item.dataset.adding = 'true';
      item.style.pointerEvents = 'none';

      const name = item.querySelector('.cd-upsell__name')?.textContent?.trim();
      const btn = item.querySelector('.cd-upsell__add');
      const originalHTML = btn ? btn.innerHTML : '';
      if (btn) {
        btn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" style="animation:pcSpin .6s linear infinite"><path d="M12 2a10 10 0 0 1 10 10"/></svg>';
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        // Re-render the drawer: adds the line item, updates the subtotal, the
        // free-shipping bar, the cart-count badges, and rebuilds the recommendations.
        // The drawer stays open. (refreshCartDrawer already updates the counts, so
        // no separate updateCartCount() round-trip is needed.)
        await refreshCartDrawer();
        announce(name ? `${name} toegevoegd aan winkelwagen` : 'Toegevoegd aan winkelwagen');

        // If the card survived the refresh - because it silently failed, or because
        // the list is only hidden now that free shipping is reached - clear its
        // loading state so it can never get stuck spinning.
        if (document.contains(item)) resetCard(item, btn, originalHTML);

        // A keyboard/screen-reader user's focus was on a card that the refresh may
        // have removed; move it to a stable element inside the drawer.
        if (viaKeyboard) {
          const drawer = document.getElementById('cart-drawer');
          const nextFocus = drawer && (
            drawer.querySelector('.cd-upsell__item') ||
            drawer.querySelector('.cart-drawer__footer a, .cart-drawer__footer button') ||
            drawer.querySelector('.cart-drawer__close')
          );
          if (nextFocus) nextFocus.focus();
        }
      } catch (error) {
        console.error('Error adding upsell item to cart:', error);
        // Restore the card so the user can retry.
        resetCard(item, btn, originalHTML);
      }
    };

    document.addEventListener('click', (e) => {
      const item = e.target.closest('.cd-upsell__item');
      if (!item) return;
      e.preventDefault();
      handleAdd(item, false);
    });

    // Keyboard support for the role="button" card (Enter / Space).
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      const item = e.target.closest('.cd-upsell__item');
      if (!item) return;
      e.preventDefault();
      handleAdd(item, true);
    });
  };

  // ==========================================================================
  // Announcement Bar
  // ==========================================================================

  const initAnnouncementBar = () => {
    const closeBtn = document.querySelector('[data-announcement-close]');
    if (!closeBtn) return;

    closeBtn.addEventListener('click', () => {
      const bar = closeBtn.closest('.announcement-bar');
      if (bar) {
        bar.classList.add('is-hidden');
        sessionStorage.setItem('announcementClosed', 'true');
      }
    });

    // Check if previously closed
    if (sessionStorage.getItem('announcementClosed') === 'true') {
      const bar = document.querySelector('.announcement-bar');
      if (bar) bar.classList.add('is-hidden');
    }
  };

  // ==========================================================================
  // Smooth Scroll
  // ==========================================================================

  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  };

  // ==========================================================================
  // Lazy Load Images
  // ==========================================================================

  const initLazyLoad = () => {
    if ('loading' in HTMLImageElement.prototype) {
      // Native lazy loading supported
      return;
    }

    // Fallback for browsers without native support
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');

    const lazyLoad = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src || img.src;
          observer.unobserve(img);
        }
      });
    });

    lazyImages.forEach(img => lazyLoad.observe(img));
  };

  // ==========================================================================
  // Cookie Banner
  // ==========================================================================

  const COOKIE_KEY = 'verdiva_cookie_consent';

  // Bij een geweigerde categorie ook de bestaande cookies ervan opruimen. De
  // scripts stoppen na de reload, maar eerder gezette cookies (_ga, _fbp,
  // _ttp, ...) blijven anders tot hun vervaldatum staan - intrekken hoort
  // ook opruimen te zijn (AVG art. 7 lid 3). Draait bij elke pageload, zodat
  // ook restanten van een eerdere, ruimere keuze verdwijnen. Functionele
  // cookies (_shopify_essential, __cf_bm, cart, localization,
  // _tracking_consent) matchen bewust geen enkele prefix.
  //
  // Twee groepen, omdat "alleen analytisch" de ene wel en de andere niet mag
  // houden.
  const ANALYTICS_COOKIES = [
    '_ga', '_gid',
    '_shopify_y', '_shopify_s', '_shopify_analytics', '_shopify_sa_',
  ];
  // Clarity en de Microsoft-cookies staan bewust in de marketinggroep: Clarity
  // meet wel analytisch, maar pingt zelf c.bing.com en MUID/_uetsid/_uetvid
  // zijn Microsoft-advertentiecookies. Daarmee hoort het geheel aan de
  // marketingkant, niet bij "alleen analytisch".
  const MARKETING_COOKIES = [
    '_gcl_au', '_fbp', '_fbc', '_ttp', 'ttcsid', '_pin_unauth',
    '_shopify_marketing',
    '_clck', '_clsk', 'MUID', '_uetsid', '_uetvid',
  ];

  // Welke groepen opruimen hangt af van de keuze: bij "alleen analytisch"
  // moeten de marketingcookies weg maar mogen de analytische blijven staan.
  const removeTrackingCookies = (groups) => {
    const prefixes = groups;
    const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    const bare = location.hostname.replace(/^www\./, '');
    document.cookie.split('; ').forEach((entry) => {
      const name = entry.split('=')[0];
      if (!name || !prefixes.some((prefix) => name.startsWith(prefix))) return;
      document.cookie = `${name}=; ${expired}`;
      document.cookie = `${name}=; ${expired}; domain=.${bare}`;
      document.cookie = `${name}=; ${expired}; domain=${location.hostname}`;
    });
  };

  // Geef de keuze door aan Shopify's Customer Privacy API. Zonder deze call
  // leeft de keuze alleen in localStorage en blijven Shopify's eigen analytics
  // en alle app-pixels na "Weigeren" gewoon draaien - de weigering is dan
  // cosmetisch. De API onthoudt niets zelf, dus we zetten het bij elke
  // pageload opnieuw op basis van de bewaarde keuze.
  const applyTrackingConsent = (value) => {
    const alles = value === 'all';
    const analytisch = alles || value === 'analytics';

    if (!alles) {
      // Marketing is in beide andere gevallen geweigerd; de analytische
      // groep alleen als er helemaal niets is toegestaan.
      const opruimen = analytisch
        ? MARKETING_COOKIES
        : MARKETING_COOKIES.concat(ANALYTICS_COOKIES);
      removeTrackingCookies(opruimen);
    }

    const consent = {
      analytics: analytisch,
      marketing: alles,
      preferences: alles,
      sale_of_data: alles,
    };

    const set = () => {
      try {
        window.Shopify.customerPrivacy.setTrackingConsent(consent, () => {});
      } catch (e) {
        /* API niet beschikbaar op deze pagina */
      }
    };

    if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
      set();
    } else if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => { if (!error) set(); }
      );
    }
  };

  const initCookieBanner = () => {
    const banner = document.getElementById('cookie-banner');
    if (!banner) return;

    let lastFocus = null;

    const open = () => {
      lastFocus = document.activeElement;
      banner.hidden = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => banner.classList.add('is-visible'));
      });
      document.getElementById('cookie-accept')?.focus();
    };

    // Verbergen in plaats van verwijderen: de banner moet heropend kunnen
    // worden via elementen met data-cookie-settings. (De footer linkt
    // inmiddels naar /pages/cookies; het mechanisme blijft als vangnet.)
    // De timeout is een vangnet voor het geval er geen transitie loopt.
    const close = () => {
      banner.classList.remove('is-visible');
      let hidden = false;
      const finish = () => {
        if (hidden) return;
        hidden = true;
        banner.hidden = true;
      };
      banner.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 500);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    };

    const dismiss = (value) => {
      let previous = null;
      try { previous = localStorage.getItem(COOKIE_KEY); } catch (e) {}
      try { localStorage.setItem(COOKIE_KEY, value); } catch (e) {}

      applyTrackingConsent(value);
      close();

      // Wisselt iemand van keuze, dan herladen. Scripts die al geladen zijn
      // (zoals Clarity) kunnen zichzelf niet terugdraaien, dus alleen een
      // verse pageload maakt het intrekken echt effectief.
      if (previous && previous !== value) {
        setTimeout(() => window.location.reload(), 300);
      }
    };

    document.getElementById('cookie-accept')?.addEventListener('click', () => dismiss('all'));
    document.getElementById('cookie-analytics')?.addEventListener('click', () => dismiss('analytics'));
    document.getElementById('cookie-reject')?.addEventListener('click', () => dismiss('necessary'));
    document.getElementById('cookie-close')?.addEventListener('click', () => dismiss('necessary'));

    document.querySelectorAll('[data-cookie-settings]').forEach((el) => {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        open();
      });
    });

    banner.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    let stored = null;
    try { stored = localStorage.getItem(COOKIE_KEY); } catch (e) {}

    if (stored) {
      applyTrackingConsent(stored);
      return;
    }

    open();
  };

  // ==========================================================================
  // Email Capture Popup
  // ==========================================================================

  const initEmailPopup = () => {
    const popup = document.querySelector('[data-email-popup]');
    if (!popup) return;

    const SUBSCRIBED_KEY = 'verdiva_popup_subscribed';
    const DISMISS_KEY    = 'verdiva_popup_dismissed_until';
    const COOKIE_KEY     = 'verdiva_cookie_consent';

    const dialog      = popup.querySelector('.email-popup__dialog');
    const form        = popup.querySelector('#EmailPopupForm');
    const emailInput  = popup.querySelector('[data-popup-email]');
    const submitBtn   = popup.querySelector('[data-popup-submit]');
    const errorEl     = popup.querySelector('[data-popup-error]');
    const formView    = popup.querySelector('[data-popup-view="form"]');
    const successView = popup.querySelector('[data-popup-view="success"]');
    const copyBtn     = popup.querySelector('[data-popup-copy]');
    const closeEls    = popup.querySelectorAll('[data-email-popup-close]');
    const openEls     = document.querySelectorAll('[data-email-popup-open]');
    const reopenTab   = document.querySelector('[data-email-popup-tab]');

    const trigger          = popup.dataset.trigger || 'smart';
    const delayMs          = (parseInt(popup.dataset.delay, 10) || 6) * 1000;
    const desktopFallbackMs = (parseInt(popup.dataset.desktopFallback, 10) || 0) * 1000;
    const scrollPct        = parseInt(popup.dataset.scroll, 10) || 50;
    const frequencyDays    = parseInt(popup.dataset.frequencyDays, 10) || 7;
    // 'immediate' bepaalt alleen dát de popup meteen opent (geen tijd/scroll/exit-intent).
    // De frequency-cap en 'al ingeschreven' gelden altijd: eenmaal weggeklikt blijft weg.

    // Geen enkele trigger mag afgaan in de eerste seconden van een pagina. Wie net
    // binnenkomt is nog aan het kijken; een venster over het scherm is dan precies
    // de ruis die we weghalen. Geldt ook voor exit-intent en de scroll-drempel:
    // op mobiel zit je met één veeg al voorbij de helft van een korte pagina.
    const MIN_DWELL_MS = 3000;
    const pageLoadedAt = Date.now();
    const dwelled = () => Date.now() - pageLoadedAt >= MIN_DWELL_MS;

    // De tijd-triggers tellen over het hele bezoek en niet per pagina. Wie
    // doorklikt naar de volgende plant begon anders steeds opnieuw bij nul en
    // haalde de 15 seconden nooit -- terwijl juist die bezoeker allang rondkijkt.
    // sessionStorage en niet localStorage: een volgend bezoek begint weer schoon.
    const SESSION_START_KEY = 'verdiva_popup_session_start';
    const sessionStart = (function () {
      var now = Date.now();
      try {
        var stored = parseInt(sessionStorage.getItem(SESSION_START_KEY), 10);
        // Een tijdstip in de toekomst kan niet kloppen (klok verzet) -> opnieuw zetten.
        if (stored && stored <= now) return stored;
        sessionStorage.setItem(SESSION_START_KEY, String(now));
      } catch (e) {}
      return now;
    })();

    let opened = false;
    let lastFocused = null;
    let listenerCtrl = null;
    let savedScrollY = 0;
    const timers = [];
    const clearTimers = () => { timers.forEach(clearTimeout); timers.length = 0; };

    // Scroll-lock die ook op iOS Safari de achtergrond vasthoudt (overflow:hidden lekt daar).
    const lockScroll = () => {
      savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    };
    const unlockScroll = () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);
    };

    // localStorage kan blokkeren (privémodus) - altijd afvangen.
    const store = {
      get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
      set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
    };

    const isCartPage = document.body.classList.contains('template-cart') ||
                       /\/(cart|checkout)(\/|$)/.test(window.location.pathname);

    const alreadySubscribed = () => store.get(SUBSCRIBED_KEY) === 'true';
    const suppressedByFrequency = () => {
      const until = parseInt(store.get(DISMISS_KEY), 10);
      return until && Date.now() < until;
    };
    // Niet over de cookiebanner heen tonen. De banner wordt niet meer uit de
    // DOM gehaald na een keuze (hij moet heropend kunnen worden via de footer),
    // dus checken we of hij op dit moment open staat - niet of hij bestaat.
    const cookieUndecided = () => {
      const banner = document.getElementById('cookie-banner');
      if (!banner) return false;
      return !banner.hidden || !store.get(COOKIE_KEY);
    };

    const canShow = () =>
      !opened && !alreadySubscribed() && !suppressedByFrequency() && !isCartPage && !cookieUndecided();

    const focusableSel = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const onKeydown = (e) => {
      if (e.key === 'Escape') { close(true); return; }
      if (e.key !== 'Tab') return;
      const focusables = Array.from(dialog.querySelectorAll(focusableSel)).filter(el => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    const focusInDialog = () => {
      let target;
      if (successView && !successView.hidden) {
        target = popup.querySelector('#email-popup-success-heading') || copyBtn || successView.querySelector(focusableSel);
      } else {
        target = emailInput;
      }
      setTimeout(() => { try { target && target.focus({ preventScroll: true }); } catch (e) {} }, 80);
    };

    const open = () => {
      if (opened) return;
      opened = true;
      clearTimers();
      if (listenerCtrl) listenerCtrl.abort(); // arm-listeners (scroll/mouseout) opruimen
      // Frequency-cap zodra de popup getoond is - ook als de bezoeker zonder klikken wegnavigeert.
      // Zo komt de popup na wegklikken niet bij elke paginalading terug.
      if (!alreadySubscribed()) {
        store.set(DISMISS_KEY, String(Date.now() + frequencyDays * 86400000));
      }
      lastFocused = document.activeElement;
      popup.hidden = false;
      popup.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => popup.classList.add('is-open'));
      });
      lockScroll();
      document.addEventListener('keydown', onKeydown);
      syncTab();
      focusInDialog();
    };

    const close = () => {
      if (!opened) return;
      opened = false;
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      unlockScroll();
      syncTab();
      document.removeEventListener('keydown', onKeydown);
      let hidden = false;
      const hideAfter = () => { if (hidden) return; hidden = true; popup.hidden = true; };
      popup.addEventListener('transitionend', hideAfter, { once: true });
      setTimeout(hideAfter, 500);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus({ preventScroll: true }); } catch (e) {}
      }
    };

    const showSuccess = () => {
      store.set(SUBSCRIBED_KEY, 'true');
      if (formView) formView.hidden = true;
      if (successView) successView.hidden = false;
      // Toegankelijke naam meeverhuizen naar de zichtbare succestitel.
      const sh = popup.querySelector('#email-popup-success-heading');
      if (dialog && sh) {
        dialog.setAttribute('aria-labelledby', 'email-popup-success-heading');
        dialog.removeAttribute('aria-describedby');
      }
    };

    // Heropen-knop verbergen terwijl de popup open is of de cookiekeuze nog loopt.
    const syncTab = () => {
      if (!reopenTab) return;
      reopenTab.classList.toggle('email-popup-tab--hidden', opened);
    };

    // Handmatig openen (knop/link) negeert de frequency-cap. Al ingeschreven? Toon de code opnieuw.
    const openManually = () => {
      if (alreadySubscribed()) {
        if (formView) formView.hidden = true;
        if (successView) successView.hidden = false;
        const sh2 = popup.querySelector('#email-popup-success-heading');
        if (dialog && sh2) {
          dialog.setAttribute('aria-labelledby', 'email-popup-success-heading');
          dialog.removeAttribute('aria-describedby');
        }
      }
      open();
    };

    // Opent zodra het mag; alleen geblokkeerd door een onbesliste cookiekeuze -> kort opnieuw proberen.
    const tryOpen = () => {
      if (opened) return false;
      if (canShow()) { open(); return true; }
      if (cookieUndecided() && !alreadySubscribed() && !suppressedByFrequency() && !isCartPage) {
        timers.push(setTimeout(tryOpen, 1200));
      }
      return false;
    };

    // ---- Triggers ----
    // Alle listeners hangen aan één AbortController; open() abort die in één keer (cleanup).
    const arm = () => {
      listenerCtrl = new AbortController();
      const signal = listenerCtrl.signal;
      const isDesktop = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)').matches;

      // De resterende tijd van het bezoek, niet van deze pagina. Is de tijd al om
      // (tweede of derde pagina), dan nog steeds niet meteen bij het binnenkomen.
      const armTime = (ms) => {
        if (ms <= 0) return;
        const remaining = ms - (Date.now() - sessionStart);
        timers.push(setTimeout(tryOpen, Math.max(MIN_DWELL_MS, remaining)));
      };

      const armScroll = () => {
        window.addEventListener('scroll', () => {
          if (!dwelled()) return;
          const docH = document.documentElement.scrollHeight - window.innerHeight;
          const pct = docH > 0 ? (window.scrollY / docH) * 100 : 0;
          if (pct >= scrollPct) tryOpen();
        }, { passive: true, signal: signal });
      };

      // Exit-intent: de muis verlaat het venster aan de bovenkant, richting het
      // kruisje of de adresbalk. relatedTarget leeg betekent dat hij het document
      // echt uit gaat en niet naar een ander element binnen de pagina.
      const armExitIntent = () => {
        document.addEventListener('mouseout', (e) => {
          if (!dwelled()) return;
          if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) tryOpen();
        }, { signal: signal });
      };

      if (trigger === 'immediate') { tryOpen(); return; }
      if (trigger === 'time')   { armTime(delayMs); return; }
      if (trigger === 'scroll') { armScroll(); return; }

      // smart
      if (isDesktop) {
        armExitIntent();
        armTime(desktopFallbackMs); // vangnet; 0 = uit
      } else {
        armTime(delayMs);
        armScroll();
      }
    };

    // ---- Submit: client-side validatie, daarna NATIVE submit ----
    // Shopify's customer-formulier werkt het betrouwbaarst met een echte form-submit. Een AJAX-POST
    // naar /contact werd door Shopify soms met 403 geweigerd -> valse rode "er ging iets mis".
    // Bij een geldig adres verwerkt Shopify de inschrijving en herlaadt de pagina met
    // ?customer_posted=true; de server-success hook (data-popup-server-success) heropent de popup
    // dan in de succesweergave met de kortingscode.
    if (form) {
      form.addEventListener('submit', function(e) {
        if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
        var email = ((emailInput && emailInput.value) || '').trim();
        var valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
        if (!valid) {
          e.preventDefault();
          if (errorEl) { errorEl.textContent = 'Vul een geldig e-mailadres in.'; errorEl.hidden = false; }
          if (emailInput) emailInput.focus();
          return;
        }
        // Geldig adres: GEEN preventDefault -> het formulier verzendt normaal (geen 403).
        if (submitBtn) { submitBtn.disabled = true; submitBtn.classList.add('is-loading'); submitBtn.setAttribute('aria-busy', 'true'); }
      });
    }

    // ---- Kortingscode kopiëren ----
    if (copyBtn) {
      copyBtn.addEventListener('click', async () => {
        const code = ((popup.querySelector('[data-popup-code-value]') || {}).textContent || '').trim();
        const label = copyBtn.querySelector('[data-popup-copy-label]');
        const done = () => {
          copyBtn.classList.add('is-copied');
          if (label) label.textContent = 'Gekopieerd';
          setTimeout(() => {
            copyBtn.classList.remove('is-copied');
            if (label) label.textContent = 'Kopieer';
          }, 2000);
        };
        try {
          await navigator.clipboard.writeText(code);
          done();
        } catch (e) {
          const ta = document.createElement('textarea');
          ta.value = code;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); done(); } catch (_) {}
          document.body.removeChild(ta);
        }
      });
    }

    // ---- Sluitknoppen + overlay ----
    closeEls.forEach(el => el.addEventListener('click', () => close()));

    // ---- Heropen-knop en handmatige open-triggers ([data-email-popup-open]) ----
    openEls.forEach(el => el.addEventListener('click', (e) => { e.preventDefault(); openManually(); }));
    syncTab();

    // ---- Server-side succes (fallback zonder JS-submit): direct in succesweergave openen ----
    if (popup.querySelector('[data-popup-server-success]')) {
      showSuccess();
      open();
      return;
    }

    // Niet bewapenen als we al weten dat we niet tonen.
    if (isCartPage) return;
    if (alreadySubscribed() || suppressedByFrequency()) return;
    arm();
  };

  // ==========================================================================
  // Mega Menu
  // ==========================================================================

  const initMegaMenu = () => {
    const header = document.querySelector('[data-header]');
    const megaItems = document.querySelectorAll('[data-mega-item]');
    const backdrop = document.querySelector('[data-mega-backdrop]');

    if (!megaItems.length) return;

    let closeTimeout;

    const openItem = (item) => {
      clearTimeout(closeTimeout);
      megaItems.forEach(other => {
        if (other !== item) {
          other.classList.remove('is-open');
          other.querySelector('.header__nav-trigger')?.setAttribute('aria-expanded', 'false');
        }
      });
      item.classList.add('is-open');
      item.querySelector('.header__nav-trigger')?.setAttribute('aria-expanded', 'true');
      header?.classList.add('has-mega-open', 'is-scrolled');
    };

    const closeItem = (item) => {
      item.classList.remove('is-open');
      item.querySelector('.header__nav-trigger')?.setAttribute('aria-expanded', 'false');
    };

    const closeAll = () => {
      megaItems.forEach(closeItem);
      header?.classList.remove('has-mega-open');
    };

    megaItems.forEach(item => {
      item.addEventListener('mouseenter', () => openItem(item));

      item.addEventListener('mouseleave', () => {
        closeTimeout = setTimeout(() => {
          closeItem(item);
          if (!Array.from(megaItems).some(i => i.classList.contains('is-open'))) {
            header?.classList.remove('has-mega-open');
          }
        }, 120);
      });

      // Click on trigger: navigate if already open, else open menu
      const trigger = item.querySelector('.header__nav-trigger');
      if (trigger) {
        trigger.addEventListener('click', (e) => {
          if (!item.classList.contains('is-open')) {
            e.preventDefault();
            openItem(item);
          }
        });
      }
    });

    backdrop?.addEventListener('click', closeAll);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAll();
    });

    // Sub-group toggles (expandable category groups)
    document.querySelectorAll('[data-mega-group]').forEach(group => {
      const trigger = group.querySelector('.header__mega-group-trigger');
      trigger?.addEventListener('click', () => {
        const isOpen = group.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', isOpen.toString());
      });
    });
  };

  // Split mega menu: hovering/focusing a left category swaps the right pane
  const initMegaSplit = () => {
    document.querySelectorAll('[data-mega-split]').forEach(split => {
      const cats = split.querySelectorAll('[data-mega-cat]');
      const panes = split.querySelectorAll('[data-mega-pane]');
      if (!cats.length || !panes.length) return;

      const activate = (key) => {
        cats.forEach(cat => {
          const on = cat.dataset.megaCat === key;
          cat.classList.toggle('is-active', on);
        });
        panes.forEach(pane => {
          pane.classList.toggle('is-active', pane.dataset.megaPane === key);
        });
      };

      cats.forEach(cat => {
        cat.addEventListener('mouseenter', () => activate(cat.dataset.megaCat));
        cat.addEventListener('focus', () => activate(cat.dataset.megaCat));
      });
    });
  };

  // Product carousel: scroll-snap rail met pijlknoppen (zakelijke landingspagina's)
  const initProductCarousel = () => {
    document.querySelectorAll('[data-product-carousel]').forEach(carousel => {
      const track = carousel.querySelector('[data-pc-track]');
      if (!track) return;

      const prev = carousel.querySelector('[data-pc-prev]');
      const next = carousel.querySelector('[data-pc-next]');
      const nav = carousel.querySelector('.prod-carousel__nav');

      const stepSize = () => {
        const slide = track.querySelector('.prod-carousel__slide');
        const gap = parseInt(getComputedStyle(track).gap, 10) || 20;
        const slideW = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
        const perView = Math.max(1, Math.floor(track.clientWidth / slideW));
        return slideW * perView;
      };

      const update = () => {
        const maxScroll = track.scrollWidth - track.clientWidth - 2;
        const overflows = track.scrollWidth > track.clientWidth + 2;
        if (nav) nav.style.display = overflows ? '' : 'none';
        if (prev) prev.disabled = track.scrollLeft <= 0;
        if (next) next.disabled = track.scrollLeft >= maxScroll;
      };

      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -stepSize(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: stepSize(), behavior: 'smooth' }));
      track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      window.addEventListener('load', update);
      update();
    });
  };

  // Scroll-reveal: elementen met .zpg-reveal glijden in beeld zodra ze zichtbaar worden
  const initScrollReveal = () => {
    const els = document.querySelectorAll('.zpg-reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-revealed');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(el => io.observe(el));
  };

  // ==========================================================================
  // Zoeken
  // ==========================================================================

  /*
   * Zoekformulieren versturen zichzelf, zonder tussenkomst van JavaScript.
   *
   * Hier stond een handler die een sterretje achter elk woord plakte voordat
   * hij naar /search navigeerde. Dat is op 11 oktober 2026 verwijderd: het
   * sterretje maakte de resultaten juist stukken slechter. Gemeten op de live
   * winkel gaf "Monster" 17 treffers en "Monster*" er 363 - zo goed als het
   * hele assortiment. Shopify doet inmiddels zelf deelwoord-matching.
   *
   * De formulieren hebben allemaal een veld q en een verborgen veld type, dus
   * een gewone GET-submit levert exact dezelfde URL op. Zie ook
   * snippets/search-query.liquid en sections/main-search.liquid.
   */

  // ==========================================================================
  // Initialize
  // ==========================================================================

  const init = () => {
    initHeader();
    initMobileMenu();
    initMegaMenu();
    initMegaSplit();
    initCookieBanner();
    initEmailPopup();
    initCartDrawer();
    attachCartItemListeners();
    initCartPageUpsellSync();
    initQuickAdd();
    initCartUpsellAdd();
    initAnnouncementBar();
    initSmoothScroll();
    initLazyLoad();
    initProductCarousel();
    initScrollReveal();
  };

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose utilities globally
  window.Verdiva = {
    formatMoney,
    updateCartCount,
    refreshCartDrawer,
    syncUpsells
  };

  /* Mobiel-optimalisatie: scroll-down detectie voor collapsing header.
     Alleen actief als body.mob-header-compact aanwezig is (via setting mob_header_compact). */
  if (document.body.classList.contains('mob-header-compact')) {
    let lastY = window.scrollY;
    let ticking = false;
    const updateScrollDir = () => {
      const y = window.scrollY;
      const delta = y - lastY;
      if (y > 100 && delta > 4) {
        document.body.classList.add('scroll-down');
      } else if (delta < -4 || y < 50) {
        document.body.classList.remove('scroll-down');
      }
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(updateScrollDir); ticking = true; }
    }, { passive: true });
  }

})();
